import { createContext, useContext, useEffect, useState } from "react";
import { planConfig } from "@/config/plans";
import { getCurrentUser, login, signup, logout } from "@/lib/api";
import { safeGetItem } from "@/lib/safeStorage";

const AuthContext = createContext({
  user: null,
  loading: true,
  plan: "free",
  planMeta: planConfig.free,
  aiCallsThisSession: 0,
  incrementAiCalls: () => {},
  signIn: async () => {},
  signUp: async () => {},
  logout: async () => {},
});

/** No token → not loading (public pages render immediately; protected routes redirect). Token → validate before rendering. */
function getInitialAuthLoading() {
  if (typeof window === "undefined") return true;
  return Boolean(safeGetItem("access_token"));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(getInitialAuthLoading);
  const [aiCallsThisSession, setAiCallsThisSession] = useState(0);

  const plan = (user?.plan || "free").toLowerCase();
  const planMeta = planConfig[plan] || planConfig.free;

  // Skip /api/web/me when logged out (any public route: landing, legal, marketing).
  useEffect(() => {
    const token = typeof window !== "undefined" ? safeGetItem("access_token") : null;
    if (!token) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const data = await getCurrentUser();
      if (data && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      // 401 is expected if not logged in
      if (error.response?.status !== 401) {
        console.error("Auth check failed:", error.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const incrementAiCalls = () => {
    setAiCallsThisSession((prev) => prev + 1);
  };

  const signIn = async ({ email, password }) => {
    try {
      const data = await login({ email, password });
      setUser(data.user);
      setAiCallsThisSession(0);
      return data;
    } catch (error) {
      const message = error.response?.data?.error || "Login failed";
      throw new Error(message);
    }
  };

  const signUp = async ({ email, password, name }) => {
    try {
      const data = await signup({ email, password, name });
      setUser(data.user);
      setAiCallsThisSession(0);
      return data;
    } catch (error) {
      const message = error.response?.data?.error || "Signup failed";
      throw new Error(message);
    }
  };

  const signOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setAiCallsThisSession(0);
      // Redirect to login or home if needed
      window.location.href = "/login";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        plan,
        planMeta,
        aiCallsThisSession,
        incrementAiCalls,
        signIn,
        signUp,
        logout: signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
